import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Dropdown, Grid } from 'semantic-ui-react';
import { FaFileAlt } from 'react-icons/fa';
import { CustoDropDownItemProps } from '../../../components/types';
import { iRootDispatch, iRootState } from '../../../store';
import PottencialBudgetForm from './forms-based-in-insurer/Pottencial';
import useEffectOnce from '../../../services/UseEffectOnce';
import { findObjectInArray } from '../../../util';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { CustomDropDownOptions } from '../../../types';
import { InsurersCodeEnum } from '../../../enums/insurers-code.enum';
import SegimobBudgetForm from './forms-based-in-insurer/Segimob';
import PortoSeguroBudgetForm from './forms-based-in-insurer/PortoSeguro';
import YelumSeguroBudgetForm from './forms-based-in-insurer/Yelum';
import AlfaBudgetForm, { BudgetContext } from './forms-based-in-insurer/Alfa';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { successNotification } from '../../../services/notification';
import GeneralCondition from './GeneralConditionModal';
import { FireInsuranceActiveInsurersResponseDTO } from '../../../dtos/fire-insurance-budget/fire-insurance-active-insurers-response.dto';
import { getFireInsuranceActiveInsurers } from '../../../services/insurer';
import GenericLogModal from '../../../components/GenericLogModal/LogModal';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import {
  InitialAddressType,
  InitialInsuredType,
  InitialTenantType
} from '../../iframe/fire-insurance-budget';
import { getBondInsuranceByID } from '../../../services/bond-insurance';
import { BondInsuranceForGetBondInsurancesResponseDTO } from '../../../dtos/bond-insurance-list/get-bond-insurances-response.dto';
import SegimobTermModal from './SegimobTermModal';
import { getSegimobTermPdf } from '../../../services/fire-insurance-budget';
import { isEstateRole } from '../../../utils/isEstateRole';
import PopupModal from '../../../components/PopupModal';
import { BannerLocationActivationEnum } from '../../../types/Banner';
import * as S from './styles';
import PwaHeader from '../../pwa-fire-insurance-budget/components/PwaHeader';

export interface FireInsuranceBudgetExtraProps {
  initialRentValue?: number | null;
  initialStartDate?: string | null;
  onBudgetCreated?: (budgetId: string) => void;
  isIframe?: boolean;
  initialAddress?: InitialAddressType;
  initialTenant?: InitialTenantType;
  initialInsured?: InitialInsuredType;
}

const w100 = {
  width: '100%'
};

const columnDistance = {
  marginBottom: 20
};

const FireInsuranceBudgetForm = (
  props: FireInsuranceBudgetFormProps & FireInsuranceBudgetExtraProps
) => {
  const { onBudgetCreated, isIframe, user, availableBrokersAndEstates } = props;
  const isEstateUser = isEstateRole(user?.role);
  const activeEstateId =
    availableBrokersAndEstates?.activeEstateId ||
    user?.activeEstateId ||
    user?.estate?.id;
  const { id: fireInsuranceAnalysisId } = useParams<{ id?: string }>();
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [bondInsuranceData, setBondInsuranceData] =
    useState<BondInsuranceForGetBondInsurancesResponseDTO | null>(null);

  const {
    context: initialContext,
    bondInsuranceId,
    estateParamId,
    insurerParamId,
    policyOriginParam
  } = useMemo(() => {
    const locationState = (location.state as any) || {};

    const bondInsuranceId =
      queryParams.get('bondInsuranceId') || locationState.bondInsuranceId;

    let context = (queryParams.get('context') as BudgetContext) || locationState.context;

    if (bondInsuranceId && !context) {
      context = 'BOND_INSURANCE' as BudgetContext;
    }

    return {
      context,
      bondInsuranceId,
      estateParamId: queryParams.get('estateId') || locationState.estateId,
      insurerParamId: queryParams.get('insurerId') || locationState.insurerId,
      policyOriginParam: queryParams.get('policyOrigin') || locationState.policyOrigin
    };
  }, [location, queryParams]);

  const [currentContext, setCurrentContext] = useState<BudgetContext>(initialContext);

  const [estateOptions, setEstateOptions] = useState<CustoDropDownItemProps[]>([]);
  const [selectedEstate, setSelectedEstate] = useState<any | null>(null);
  const [insurerOptions, setInsurerOptions] = useState<CustoDropDownItemProps[]>([]);
  const [LoadingInsurerOptions, LoadingSetInsurerOptions] = useState<boolean>(false);
  const [selectedInsurer, setSelectedInsurer] = useState<any | null>(null);
  const [availableInsurers, setAvailableInsurers] = useState<any[] | null>(null);
  const [insurerId, setInsurerId] = useState<string | null>(null);
  const [commissionPercentage, setCommissionPercentage] = useState<number>(0);
  const [openConditions, setOpenConditions] = useState<boolean>(false);
  const [dropDownSelectEstateIsOpen, setDropDownSelectEstateIsOpen] =
    useState<boolean>(false);
  const [openSegimobTermModal, setOpenSegimobTermModal] = useState<boolean>(false);
  const [segimobTermPdf, setSegimobTermPdf] = useState<string | null>(null);

  const getEnabledInsurer = useCallback(
    async (selectedEstateId: string) => {
      if (insurerParamId) {
        return;
      }
      LoadingSetInsurerOptions(true);

      try {
        const response: FireInsuranceActiveInsurersResponseDTO =
          await getFireInsuranceActiveInsurers(selectedEstateId);
        setAvailableInsurers(response.fire_insurance_active_insurers);

        const insurers = response.fire_insurance_active_insurers.map((insurer: any) => ({
          key: insurer.id,
          text: insurer.name,
          value: insurer,
          image: insurer.image_url
        }));

        setInsurerOptions(insurers);
      } catch (error) {
        console.log(error);
      } finally {
        LoadingSetInsurerOptions(false);
      }
    },
    [insurerParamId]
  );

  useEffectOnce(() => {
    if (availableBrokersAndEstates) {
      setEstateOptions(availableBrokersAndEstates.estates);
    }
  });

  const loadData = useCallback(async () => {
    if (fireInsuranceAnalysisId) {
      try {
        if (fireInsuranceAnalysisId.length !== 36) {
          history.push('/admin/fire-insurance-budget');
          return;
        }

        const estate = availableBrokersAndEstates.estates?.find(
          c => c.value.id === estateParamId
        );

        if (estate) {
          handleSelectEstate(estate.value);
        }
      } catch (err) {
        console.log(err);
      }
    }
  }, [
    fireInsuranceAnalysisId,
    estateParamId,
    history,
    availableBrokersAndEstates.estates
  ]);

  const loadBondInsuranceData = useCallback(
    async (bondInsuranceId: string) => {
      try {
        const response = await getBondInsuranceByID(bondInsuranceId);
        setBondInsuranceData(response);

        const estateId = response?.estate?.id || response?.bond_insurance?.estate?.id;

        if (estateId) {
          const estate = availableBrokersAndEstates.estates?.find(
            c => c.value.id === estateId
          );

          if (estate) {
            handleSelectEstate(estate.value);
          }
        }

        successNotification(
          'Seguro Fiança',
          'Dados do seguro fiança carregados com sucesso'
        );
      } catch (error) {
        console.error('Erro ao carregar dados do seguro fiança:', error);
      }
    },
    [availableBrokersAndEstates.estates]
  );

  useEffect(() => {
    const isBondInsuranceContext = !!bondInsuranceId;

    if (isBondInsuranceContext) {
      loadBondInsuranceData(bondInsuranceId);
    } else {
      loadData();
    }
  }, [bondInsuranceId, loadBondInsuranceData, loadData]);

  const handleSelectEstate = useCallback(
    async (estate: any) => {
      setSelectedEstate(estate);
      setSegimobTermPdf(null);

      // Verifica se precisa verificar o termo de aceite
      if (!estate.segimob_term) {
        try {
          // Faz requisição silenciosa para buscar o PDF
          const pdfData = await getSegimobTermPdf(estate.id);

          if (pdfData && pdfData.data) {
            // Tem PDF pendente, prepara para exibir modal
            setSegimobTermPdf(pdfData.data);
            setOpenSegimobTermModal(true);
          } else {
            // Não tem PDF (termo já aceito), atualiza Redux
            const updatedEstates = availableBrokersAndEstates.estates.map((est: any) => {
              if (est.value.id === estate.id) {
                return {
                  ...est,
                  value: {
                    ...est.value,
                    segimob_term: true
                  }
                };
              }
              return est;
            });

            props.updateAvailableBrokersAndEstates({
              ...availableBrokersAndEstates,
              estates: updatedEstates
            });
          }
        } catch (error) {
          // Em caso de erro, não exibe modal e não mostra erro para usuário
          // Apenas continua o fluxo normal
        }
      }

      if (Array.isArray(estate.fireInsurers)) {
        const insurers = estate.fireInsurers.map((insurer: any) => ({
          key: insurer.id,
          text: insurer.name,
          value: insurer,
          image: insurer.imageUrl
        }));

        if (insurerParamId) {
          const insurer = insurers.find(
            (insurer: any) => insurer.value.id === insurerParamId
          );
          if (insurer) {
            successNotification(
              'Seguradora selecionada',
              `Seguradora ${insurer?.text} selecionada`
            );
            setInsurerOptions([insurer]);
            return;
          }
        }

        setInsurerOptions(insurers);
      }

      setDropDownSelectEstateIsOpen(true);
    },
    [insurerParamId, availableBrokersAndEstates, props]
  );

  useEffect(() => {
    if (estateOptions.length === 1) {
      const estate = estateOptions[0].value;
      setSelectedEstate(estate);
      setDropDownSelectEstateIsOpen(true);
    }
  }, [estateOptions]);

  useEffect(() => {
    if (!isEstateUser || !estateOptions.length) {
      return;
    }

    const activeEstate =
      estateOptions.find(estate => estate.value.id === activeEstateId) || estateOptions[0];
    const nextEstate = activeEstate?.value;

    if (nextEstate && selectedEstate?.id !== nextEstate.id) {
      handleSelectEstate(nextEstate);
    }
  }, [activeEstateId, estateOptions, handleSelectEstate, isEstateUser, selectedEstate?.id]);

  useEffect(() => {
    if (selectedEstate !== null) {
      getEnabledInsurer(selectedEstate.id);
    } else if (selectedEstate === '') {
      setSelectedEstate(null);
      setAvailableInsurers(null);
      setDropDownSelectEstateIsOpen(false);
    }
  }, [selectedEstate, getEnabledInsurer]);

  useEffect(() => {
    if (!insurerParamId && availableInsurers) {
      setSelectedInsurer(null);
      setInsurerId(null);
      if (availableInsurers.length === 1) {
        setSelectedInsurer(availableInsurers[0]);
        setInsurerId(availableInsurers[0].id);
      }
    }
  }, [availableInsurers, insurerParamId]);

  useEffect(() => {
    if (insurerOptions.length === 1) {
      setSelectedInsurer(insurerOptions[0].value);
      setInsurerId(insurerOptions[0].value.id);
    }
  }, [insurerOptions]);

  const handleSelectInsurer = useCallback(
    async (insurer: any) => {
      if (!!availableBrokersAndEstates.estates.length && selectedEstate) {
        const getThisEstate = availableBrokersAndEstates.estates.find(
          (estate: CustomDropDownOptions) => estate.value.id === selectedEstate.id
        )?.value;

        if (getThisEstate && getThisEstate.insurer_data) {
          const getThisInsurer = findObjectInArray(
            getThisEstate.insurer_data,
            'insurer_id',
            insurer.id
          );

          const percentage = Number(getThisInsurer?.commission_percentage) || 0;
          setCommissionPercentage(percentage);
        }
      }

      setSelectedInsurer(insurer);
      setInsurerId(insurer.id);
    },
    [availableBrokersAndEstates.estates, selectedEstate]
  );

  const toggleOpenConditions = useCallback(() => {
    setOpenConditions(true);
  }, []);

  const toggleCloseConditions = useCallback(() => {
    setOpenConditions(false);
  }, []);

  const renderedForm = useMemo(() => {
    if (
      selectedEstate === null ||
      selectedInsurer === null ||
      selectedInsurer.code === null
    ) {
      return null;
    }

    const formContext = currentContext || BudgetContext.new;

    const initialRentValue = bondInsuranceData?.property?.rent_value;
    const initialStartDate = bondInsuranceData?.property?.rental_period_start_date;
    const bondInitialAddress = bondInsuranceData?.property
      ? {
          neighborhood: bondInsuranceData.property.district,
          city: bondInsuranceData.property.city,
          state: bondInsuranceData.property.state,
          zipCode: bondInsuranceData.property.zip_code,
          street: bondInsuranceData.property.street,
          number: bondInsuranceData.property.number,
          complement: bondInsuranceData.property.complement || null
        }
      : undefined;

    const initialAddress = bondInitialAddress;

    const initialTenant = bondInsuranceData?.natural_tenant
      ? {
          name: bondInsuranceData.natural_tenant.main_tenant_name,
          document: bondInsuranceData.natural_tenant.main_tenant_cpf,
          phoneNumber: bondInsuranceData.natural_tenant.main_tenant_phone,
          email: bondInsuranceData.natural_tenant.main_tenant_email,
          birthdate: String(bondInsuranceData.natural_tenant.main_tenant_birth_date)
        }
      : bondInsuranceData?.legal_tenant
      ? {
          name: bondInsuranceData.legal_tenant.company,
          document: bondInsuranceData.legal_tenant.document,
          phoneNumber: bondInsuranceData.legal_tenant.phone,
          email: bondInsuranceData.legal_tenant.email,
          birthdate: ''
        }
      : undefined;

    const initialInsured = bondInsuranceData?.property_owner_natural_person
      ? {
          name: bondInsuranceData.property_owner_natural_person.name,
          document:
            bondInsuranceData.property_owner_natural_person.cpf ||
            bondInsuranceData.property_owner_natural_person.cnpj
        }
      : undefined;

    switch (selectedInsurer.code) {
      case InsurersCodeEnum.Pottencial:
        return (
          <PottencialBudgetForm
            estateId={selectedEstate.id}
            insurerId={insurerId!}
            userId={user.id}
            percentage={commissionPercentage}
            initialRentValue={initialRentValue ? Number(initialRentValue) : undefined}
            initialStartDate={
              initialStartDate
                ? typeof initialStartDate === 'object' && initialStartDate instanceof Date
                  ? initialStartDate.toISOString()
                  : String(initialStartDate)
                : undefined
            }
            onBudgetCreated={onBudgetCreated}
            isIframe={isIframe}
          />
        );
      case InsurersCodeEnum.TokioMarineSegimob:
        return (
          <SegimobBudgetForm
            bondInsuranceId={bondInsuranceId}
            estate={selectedEstate}
            insurerId={insurerId!}
            userId={user.id}
            percentage={commissionPercentage}
            initialRentValue={initialRentValue ? Number(initialRentValue) : undefined}
            initialStartDate={
              initialStartDate
                ? typeof initialStartDate === 'object' && initialStartDate instanceof Date
                  ? initialStartDate.toISOString()
                  : String(initialStartDate)
                : undefined
            }
            onBudgetCreated={onBudgetCreated}
            isIframe={isIframe}
            initialAddress={initialAddress}
            initialTenant={initialTenant}
            initialInsured={{
              document: initialInsured?.document || '',
              email: null,
              name: initialInsured?.name || '',
              phoneNumber: null
            }}
          />
        );
      case InsurersCodeEnum.Porto:
        return (
          <PortoSeguroBudgetForm
            estate={selectedEstate}
            insurerId={insurerId!}
            userId={user.id}
            percentage={commissionPercentage}
            initialRentValue={initialRentValue ? Number(initialRentValue) : undefined}
            initialStartDate={
              initialStartDate
                ? typeof initialStartDate === 'object' && initialStartDate instanceof Date
                  ? initialStartDate.toISOString()
                  : String(initialStartDate)
                : undefined
            }
            onBudgetCreated={onBudgetCreated}
            isIframe={isIframe}
          />
        );
      case InsurersCodeEnum.alfa:
        return (
          <AlfaBudgetForm
            key={selectedEstate?.id}
            bondInsuranceId={bondInsuranceId}
            estate={selectedEstate}
            insurerId={insurerId!}
            userId={user.id}
            context={formContext}
            policyOrigin={policyOriginParam}
            budgetId={fireInsuranceAnalysisId}
            percentage={commissionPercentage}
            initialRentValue={initialRentValue ? Number(initialRentValue) : undefined}
            initialStartDate={
              initialStartDate
                ? typeof initialStartDate === 'object' && initialStartDate instanceof Date
                  ? initialStartDate.toISOString()
                  : String(initialStartDate)
                : undefined
            }
            onBudgetCreated={onBudgetCreated}
            isIframe={isIframe}
            initialAddress={initialAddress}
            initialTenant={initialTenant}
            initialInsured={{
              document: initialInsured?.document || '',
              email: null,
              name: initialInsured?.name || '',
              phoneNumber: null
            }}
          />
        );
      case InsurersCodeEnum.Yelum:
        return (
          <YelumSeguroBudgetForm
            bondInsuranceId={bondInsuranceId}
            estate={selectedEstate}
            insurerId={insurerId!}
            userId={user.id}
            context={formContext}
            policyOrigin={policyOriginParam}
            budgetId={fireInsuranceAnalysisId}
            percentage={commissionPercentage}
            initialRentValue={initialRentValue ? Number(initialRentValue) : undefined}
            initialStartDate={
              initialStartDate
                ? typeof initialStartDate === 'object' && initialStartDate instanceof Date
                  ? initialStartDate.toISOString()
                  : String(initialStartDate)
                : undefined
            }
            onBudgetCreated={onBudgetCreated}
            isIframe={isIframe}
            initialAddress={initialAddress}
            initialTenant={initialTenant}
            initialInsured={{
              document: initialInsured?.document || '',
              email: null,
              name: initialInsured?.name || '',
              phoneNumber: null
            }}
          />
        );
      default:
        return <h1>Acesso negado!</h1>;
    }
  }, [
    selectedEstate,
    selectedInsurer,
    currentContext,
    insurerId,
    user.id,
    commissionPercentage,
    onBudgetCreated,
    isIframe,
    policyOriginParam,
    fireInsuranceAnalysisId,
    bondInsuranceData
  ]);

  useEffect(() => {
    setCurrentContext(initialContext);
  }, [initialContext]);

  const shouldShowForm =
    selectedInsurer !== null &&
    selectedEstate !== null &&
    ([BudgetContext.editing, BudgetContext.copyCanceled].includes(currentContext) ||
      dropDownSelectEstateIsOpen);

  const handleMobileBack = useCallback(() => {
    if (selectedInsurer && !fireInsuranceAnalysisId) {
      setSelectedInsurer(null);
      setInsurerId(null);
      setDropDownSelectEstateIsOpen(true);
      return;
    }

    history.goBack();
  }, [fireInsuranceAnalysisId, history, selectedInsurer]);

  return (
    <S.BudgetPage>
      <S.MobileBudgetHeader>
        <PwaHeader
          onBack={handleMobileBack}
          title={selectedInsurer ? 'Dados para cotação' : 'Novo orçamento'}
        />
      </S.MobileBudgetHeader>

      {!selectedInsurer && (
        <S.MobileBudgetIntro>
          <S.MobileBudgetTitle>
            Novo orçamento
            <S.MobileBudgetTitleRule />
            <FaFileAlt aria-hidden="true" />
          </S.MobileBudgetTitle>
        </S.MobileBudgetIntro>
      )}

      <Grid style={w100} centered className="budget-page-grid">
        <Grid.Row
          className="budget-page-actions"
          style={{ textAlign: 'right', justifyContent: 'right', gap: 20 }}
        >
          {user.role === UserRolesEnum.brokerAdmin && selectedEstate && (
            <GenericLogModal
              relatedIds={[selectedEstate?.id]}
              createdAt={new Date(
                new Date().setDate(new Date().getDate() - 30)
              ).toISOString()}
              context={'FIRE_INSURANCE'}
            />
          )}
          <GeneralCondition
            openModal={openConditions}
            toggleOpen={toggleOpenConditions}
            toggleClose={toggleCloseConditions}
          />
        </Grid.Row>

        <Grid.Row className="budget-page-selectors" style={{ paddingTop: 0 }}>
          {!isEstateUser && (
            <Grid.Column mobile={16} tablet={6} computer={6} style={columnDistance}>
              <S.BudgetFieldLabel>Imobiliária</S.BudgetFieldLabel>
              <Dropdown
                fluid
                search
                clearable
                selection
                options={estateOptions}
                placeholder="Selecione a imobiliária..."
                disabled={estateOptions.length === 1}
                value={selectedEstate}
                onChange={(_, { value }) => handleSelectEstate(value)}
                onOpen={() => setDropDownSelectEstateIsOpen(false)}
                onClose={() => setDropDownSelectEstateIsOpen(true)}
              />
            </Grid.Column>
          )}

          <Grid.Column mobile={16} tablet={6} computer={6} style={columnDistance}>
            <S.BudgetFieldLabel>Seguradora</S.BudgetFieldLabel>
            <Dropdown
              fluid
              search
              clearable
              options={insurerOptions}
              selection
              placeholder="Selecione a seguradora..."
              disabled={!selectedEstate}
              value={selectedInsurer}
              loading={LoadingInsurerOptions}
              onChange={(_, { value }) => handleSelectInsurer(value)}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {!selectedInsurer && (
        <S.MobileBudgetFooter>
          <S.MobileBudgetBackButton type="button" onClick={() => history.goBack()}>
            Voltar
          </S.MobileBudgetBackButton>
          <S.MobileBudgetNextButton
            type="button"
            onClick={() => setDropDownSelectEstateIsOpen(true)}
          >
            Próximo
          </S.MobileBudgetNextButton>
        </S.MobileBudgetFooter>
      )}

      <div className="budget-form-container">{shouldShowForm ? renderedForm : null}</div>

      {openSegimobTermModal && segimobTermPdf && (
        <SegimobTermModal
          open={openSegimobTermModal}
          estateId={selectedEstate?.id || ''}
          segimobCode={selectedEstate?.segimob_code || 0}
          pdfData={segimobTermPdf}
          onClose={() => {
            setOpenSegimobTermModal(false);
            setSegimobTermPdf(null);
          }}
          onTermAccepted={updatedEstate => {
            // Atualiza Redux após aceitar termo
            const updatedEstates = availableBrokersAndEstates.estates.map((est: any) => {
              if (est.value.id === updatedEstate.id) {
                return {
                  ...est,
                  value: {
                    ...est.value,
                    segimob_term: true
                  }
                };
              }
              return est;
            });

            props.updateAvailableBrokersAndEstates({
              ...availableBrokersAndEstates,
              estates: updatedEstates
            });

            setOpenSegimobTermModal(false);
            setSegimobTermPdf(null);
          }}
        />
      )}

      <PopupModal
        locationActivation={BannerLocationActivationEnum.FIRE_INSURANCE_NEW_BUDGET}
      />
    </S.BudgetPage>
  );
};

const mapStateToProps = (state: iRootState) => ({
  scroll: state.scroll,
  user: state.user,
  insurers: state.insurers,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

const mapDispatchToProps = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

export default connect(mapStateToProps, mapDispatchToProps)(FireInsuranceBudgetForm);
export type FireInsuranceBudgetFormProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;
